import { useEffect, useRef, useState } from 'react';

import useAccountQuery from 'api/queries/useAccountQuery';
import useDiscountLadderQuery from 'api/queries/useDiscountLadderQuery';
import { trackEvent } from 'utils/analyticsUtils';

import { getSelectedDiscount } from './utils';

const localStorageKey = 'CUSTOMER_PROMO_NANO_BANNER_DISMISSED';

/**
 * Custom hook that manages the display logic and state for customer promo nano banners.
 *
 * This hook handles:
 * - Fetching account and discount data
 * - Determining when to show the promo banner based on available discounts
 * - Managing banner dismissal state per charge ID
 * - Tracking banner exposure and dismissal events
 * - Persisting dismissal state in localStorage
 *
 * The banner is shown when:
 * - User has not dismissed it for the current charge ID
 * - Data is not loading
 * - A selected discount exists with nanobar text and terms
 *
 * @returns {Object} Hook return object
 * @returns {Object} .selectedDiscount - The selected discount object with nanobar properties
 * @returns {Object} .discountsData - The complete discounts data from the API
 * @returns {boolean} .isDiscountsDataLoading - Whether the discounts data is currently loading
 * @returns {Function} .handleDismiss - Function to handle dismissing the banner and tracking the event
 * @returns {boolean} .isDismissedForChargeID - Whether the banner has been dismissed for the current charge ID
 * @returns {boolean} .showCustomerPromoNanoBanner - Whether the customer promo nano banner should be shown
 */
function useCustomerPromoNanoBanner() {
    const trackedRef = useRef(false);

    const [isDismissedForChargeID, setIsDismissedForChargeID] = useState(true);

    const { data: accountData, isLoading: isAccountDataLoading } = useAccountQuery();
    const { data: discountsData, isLoading: isDiscountsDataLoading } = useDiscountLadderQuery();

    const isDataLoading = isAccountDataLoading || isDiscountsDataLoading;

    const selectedDiscount = getSelectedDiscount(discountsData);

    // page exclusions
    const pathname = window?.location?.pathname;
    const isAppCart = window.isNativeApp && pathname === '/cart';
    const isStorefrontConfigPreview = pathname?.startsWith('/storefront-config');
    const isExcludedPage = isStorefrontConfigPreview || isAppCart;

    const showCustomerPromoNanoBanner = Boolean(
        !isExcludedPage &&
        !isDismissedForChargeID &&
        !isDataLoading &&
        !!selectedDiscount &&
        selectedDiscount?.nanobarText &&
        selectedDiscount?.nanobarTerms
    );

    function handleDismiss() {
        localStorage.setItem(localStorageKey, accountData?.nextOrder?.chargeID);
        setIsDismissedForChargeID(true);
        trackEvent('promo banner dismiss', {
            path: window.location.pathname,
            promo_code: selectedDiscount?.discountCode,
        });
    }

    useEffect(() => {
        if (!isDataLoading) {
            const dismissedLocalStorage = localStorage.getItem(localStorageKey);
            // eslint-disable-next-line eqeqeq
            setIsDismissedForChargeID(dismissedLocalStorage == accountData?.nextOrder?.chargeID);
        }
    }, [isDataLoading]);

    useEffect(() => {
        if (!trackedRef.current && showCustomerPromoNanoBanner) {
            trackedRef.current = true;

            trackEvent('promo banner exposure', {
                path: window.location.pathname,
                promo_code: selectedDiscount?.discountCode,
            });
        }
    }, [showCustomerPromoNanoBanner]);

    return {
        selectedDiscount,
        discountsData,
        isDiscountsDataLoading,
        handleDismiss,
        isDismissedForChargeID,
        showCustomerPromoNanoBanner,
    };
}

export default useCustomerPromoNanoBanner;
